import type { AcceptIngest, VatCreditStatus } from '@dcc-erp/shared';

import type { LineRow } from './LineItemsGrid';

// BE -> AD (mirrors the API beToAd heuristic: years >= 2400 are Buddhist-era).
function beToAdYear(year: number): number {
  return year >= 2400 ? year - 543 : year;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Convert a 'YYYY-MM-DD'-ish Buddhist-or-Gregorian date to Gregorian ISO. */
export function beDateToIsoAd(raw: string | null): string | null {
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
  if (!m) return null;
  const y = beToAdYear(Number(m[1]));
  const d = new Date(Date.UTC(y, Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Add `days` to an ISO date, returning ISO (UTC, no DST drift). */
export function addDaysIso(iso: string, days: number): string {
  const [y, mo, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, mo - 1, d + days));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

function todayIso(): string {
  const n = new Date();
  return `${n.getUTCFullYear()}-${pad(n.getUTCMonth() + 1)}-${pad(n.getUTCDate())}`;
}

export interface BuildAcceptArgs {
  vendorId: string;
  vatCreditStatus: VatCreditStatus;
  invoiceDateRaw: string | null;
  termsDays: number;
  vendorInvoiceNo?: string;
  branchId?: string | null;
  departmentId?: string | null;
  lines: LineRow[];
  // WHT INTENT ONLY (spec §15.4): captured for the bill so the cert can be issued
  // at PAYMENT by the existing WHT module. Never issues a cert here. These flow
  // straight into the optional AcceptIngest WHT fields when present.
  whtIncomeType?: string | null;
  whtRate?: number | null;
  whtBase?: number | null;
}

export function buildAcceptPayload(args: BuildAcceptArgs): AcceptIngest {
  const billDate = beDateToIsoAd(args.invoiceDateRaw) ?? todayIso();
  const dueDate = addDaysIso(billDate, Math.max(0, args.termsDays || 0));
  return {
    vendorId: args.vendorId,
    billDate,
    dueDate,
    vatCreditStatus: args.vatCreditStatus,
    ...(args.branchId !== undefined ? { branchId: args.branchId } : {}),
    ...(args.departmentId !== undefined ? { departmentId: args.departmentId } : {}),
    ...(args.vendorInvoiceNo ? { vendorInvoiceNo: args.vendorInvoiceNo } : {}),
    // Spread WHT intent only when supplied so we never write `undefined` onto an
    // optional field (exactOptionalPropertyTypes) and keep the no-intent payload clean.
    ...(args.whtIncomeType != null ? { whtIncomeType: args.whtIncomeType } : {}),
    ...(args.whtRate != null ? { whtRate: args.whtRate } : {}),
    ...(args.whtBase != null ? { whtBase: args.whtBase } : {}),
    lines: args.lines.map((l) => ({
      description: l.description,
      qty: Number(l.qty) || 1,
      unitPrice: Number(l.unitPrice) || 0,
      expenseCode: l.expenseCode,
    })),
  };
}
